import { eventBus } from '@/shared/EventBus';
import { useGameStore } from '@/ui/store/gameStore';
import { saveManager } from './index';
import { economyApi, ApiError } from '@/net/api';
import { ENABLED_BIOMES } from '@/config/biomes.config';
import { addCoins } from '@/domain/economy/coins';

/**
 * AutoSaveBridge — batchuje aktywność świata (monety + zniszczone obiekty) i
 * wysyła ją do serwera przez `POST /world/tick`.
 *
 * **Sesja 4.D.3 — coins server-authoritative.** Wcześniej bridge robił blob
 * `PUT /saves/me` z totalem `coins`. Teraz `PUT` NIE zapisuje `coins`
 * (writeSave skipEntities merge'uje z DB) — jedyną drogą kredytowania monet
 * jest `/world/tick`, który DODAJE deltę (append-only). Bridge akumuluje:
 *   - `coinsEarnedSinceLastFlush` — suma `coins:earned` (realny zarobek)
 *   - `destroyedSinceLastFlush`   — liczba `destructible:destroyed`
 * i co `DEBOUNCE_MS` / `INTERVAL_MS` / przy `visibility:hidden` flushuje je
 * jednym tickiem. Po sukcesie PATCHUJE zwrócone skalary (currencies/stats/
 * gemsRolled) do lokalnego save'a — ekwipunek (pety/jajka) zostaje nietknięty
 * (Faza 1a dieta payloadu, DESIGN_PERFORMANCE_SCALING.md §A1).
 *
 * **Reconciliation** — `/world/tick` zwraca `currencies.coins` serwera SPRZED
 * monet zebranych w trakcie requestu (te trafiły do zresetowanego akumulatora).
 * Display = `currencies.coins + coinsEarnedSinceLastFlush` → brak flickera, brak
 * utraty. `getPendingCoinsDelta()` udostępnia akumulator `economyActions`,
 * żeby sync po akcji ekonomii też reconcile'ował.
 *
 * **Single-flight przez Promise chain** (`flushLock`) — kolejne flushe się
 * serializują. **Pauza** (`pauseAutoSave`) — `runEconomyAction` (sesja 35B)
 * blokuje tick na czas granularnej akcji ekonomii.
 *
 * **Brak `beforeunload`** — `visibilitychange:hidden` jest pewniejszy.
 * **HMR safety** — `import.meta.hot.dispose()` clearuje timery + listenery.
 */

// Debounce 3s / interval 10s — kompromis: max ~3s utraty progresu przy HARD
// crash (zamknięcie karty flushuje natychmiast przez visibility-handler).
const DEBOUNCE_MS = 3000;
const INTERVAL_MS = 10_000;

/**
 * Maksymalny coins-delta wysyłany w JEDNYM `/world/tick`. MUSI być <= serwerowemu
 * `MAX_COINS_PER_TICK` (server/api/src/economy/schemas.ts) — używamy TEJ SAMEJ
 * formuły tier-scaled (×10/tier), więc oba dają tę samą wartość (tier 6 → 1e12).
 *
 * Po co: serwer odbija 400, gdy coinsEarned > cap. Bez tego ograniczenia
 * pojedynczy ogromny tick (biom Fast Food + mikstura złota) był nie do wysłania,
 * a `doFlush` resendował go w kółko (ten sam pendingTick) — to ZATYKAŁO cały
 * pipeline monet i rozjeżdżało HUD z serwerem. Tniemy deltę na porcje ≤ cap;
 * nadwyżka zostaje w akumulatorze i leci następnym flushem (zero utraty monet).
 */
// ENABLED_BIOMES (nie pełne BIOMES) — klientowy cap MUSI śledzić to samo, co serwer
// liczy z SERVER_BIOMES. Biom z `enabled:false` (np. Atlantyda przed go-live) nie
// istnieje na serwerze, więc nie może podbijać klientowego capu ponad serwerowy
// (inaczej klient wyśle tick > serwerowy cap → 400 → zatkany pipeline). Po go-live
// (enabled:true + SERVER_BIOMES) oba znów liczą ten sam max tier.
const MAX_BIOME_TIER = Math.max(...ENABLED_BIOMES.map((b) => b.tier));
const MAX_COINS_PER_TICK = 10_000_000n * 10n ** BigInt(MAX_BIOME_TIER - 1);

let installed = false;
let dirty = false;
/**
 * Pauza autosave (sesja 35B). Gdy `true`, `doFlush` jest no-opem (zachowuje
 * `dirty` + akumulatory). `runEconomyAction` pauzuje bridge na czas requestu —
 * world tick nie wyścignie się z granularną akcją ekonomii.
 */
let paused = false;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let intervalTimer: ReturnType<typeof setInterval> | null = null;
/** Liczba zniszczonych destructibli od ostatniego flusha. */
let destroyedSinceLastFlush = 0;
/**
 * Etap 5, 5.0.B: breakdown zniszczonych destruktibli per typ (do server-side
 * gem roll). Klient wysyła `destroyedByType`, serwer roluje gems wg
 * `DESTRUCTIBLE_GEM_REWARDS` z `game-config/destructibles.ts`.
 *
 * Trzymamy OBOK `destroyedSinceLastFlush` (nie zastępujemy) bo total count
 * idzie do innego pola (`objectsDestroyed`) i ma sanity cap niezależny.
 * Plus to gwarancja że stari klienci (sprzed 5.0.B) mają backward compat —
 * destroyedByType jest opcjonalne w schemacie.
 */
let destroyedByTypeSinceLastFlush: Record<string, number> = {};
/** Suma monet ZEBRANYCH w świecie od ostatniego flusha (delta dla /world/tick). */
let coinsEarnedSinceLastFlush = 0n;
/**
 * Maksymalny wiek `pendingTick` zanim go porzucimy (Codex 4.D.3 FIXES² MED 1).
 * Serwer prune'uje rejestr `world_ticks` po 24h — retry ticka starszego niż to
 * ryzykowałby podwójny kredyt (wpis mógł już zniknąć). Klient porzuca pendingTick
 * po 6h (4× zapas pod retencję serwera), godząc się na utratę delty jednego
 * ticka po >6h ciągłego offline — pomijalne (gracz i tak wtedy nie grał).
 */
const PENDING_TICK_MAX_AGE_MS = 6 * 60 * 60 * 1000;

/**
 * Tick wysłany do `/world/tick` ale jeszcze NIE potwierdzony (sesja 4.D.3
 * FIXES — Codex HIGH 1). Po błędzie kolejny flush resend-uje DOKŁADNIE ten
 * sam obiekt (ten sam `tickId` + delty) — serwer dedupuje przez tabelę
 * `world_ticks`. NIE merge'ujemy delty z powrotem do akumulatora — to
 * złamałoby idempotency (endpoint append-only, retry po commitcie podwoiłby
 * monety). `createdAt` — gdy pendingTick starszy niż PENDING_TICK_MAX_AGE_MS,
 * porzucamy go zamiast retry (rejestr serwera mógł już wygasnąć).
 */
let pendingTick:
  | {
      tickId: string;
      coinsDelta: bigint;
      destroyedDelta: number;
      destroyedByType: Record<string, number>;
      createdAt: number;
    }
  | null = null;
let unsubscribers: Array<() => void> = [];

/**
 * Promise chain — kolejne flushe się serializują, eliminuje re-entrancy.
 * Snapshot delt odbywa się DOPIERO wewnątrz `doFlush`, gdy poprzedni tick
 * już zaadoptował save.
 */
let flushLock: Promise<void> = Promise.resolve();

/** Generuje `tickId` (idempotency key) — UUID, fallback dla starych browserów. */
function makeTickId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tick-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function markDirty(): void {
  dirty = true;
  if (debounceTimer != null) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => { void flush('debounce'); }, DEBOUNCE_MS);
}

/** Public flush — linkuje się na koniec Promise chain. */
function flush(reason: string): Promise<void> {
  // .catch(()=>{}) — błąd jednego flusha nie blokuje kolejnych.
  const next = flushLock.catch(() => {}).then(() => doFlush(reason));
  flushLock = next;
  return next;
}

/**
 * Faktyczny flush — wysyła `POST /world/tick` z deltą i patchuje zwrócone
 * skalary (currencies/stats/gemsRolled) do lokalnego save'a (Faza 1a — bez
 * ekwipunku).
 *
 * Idempotency (4.D.3 FIXES): nieudany `pendingTick` jest resend-owany DOKŁADNIE
 * w tej samej formie (ten sam `tickId`); dopiero gdy nie ma pendingTick,
 * snapshotujemy świeże akumulatory jako NOWY tick. Bez tego retry po udanym
 * commitcie (zgubiona odpowiedź) podwoiłby monety.
 */
async function doFlush(reason: string): Promise<void> {
  // Pauza (sesja 35B) — granularna akcja ekonomii w toku. Zachowujemy `dirty`,
  // `pendingTick` + akumulatory; `resumeAutoSave` przeharmonogramuje flush.
  if (paused) return;
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  // Porzuć przeterminowany pendingTick — rejestr `world_ticks` na serwerze
  // mógł już wygasnąć (retencja 24h), więc retry ryzykowałby podwójny kredyt.
  if (pendingTick && Date.now() - pendingTick.createdAt > PENDING_TICK_MAX_AGE_MS) {
    console.warn('[AutoSave] porzucam przeterminowany pendingTick:', pendingTick.tickId);
    pendingTick = null;
  }

  // Wyznacz tick do wysłania.
  let tick: {
    tickId: string;
    coinsDelta: bigint;
    destroyedDelta: number;
    destroyedByType: Record<string, number>;
    createdAt: number;
  };
  if (pendingTick) {
    // Poprzedni tick nieudany — resend TEN SAM (serwer go zdedupuje jeśli
    // jednak się zacommitował).
    tick = pendingTick;
  } else {
    if (!dirty) return;
    // Snap synchronicznie ZANIM await — kolejne eventy w trakcie ticka znów
    // ustawią dirty=true i dorzucą do (wyzerowanych) akumulatorów.
    dirty = false;
    // Cap coins na JEDEN tick — nadwyżkę ponad MAX_COINS_PER_TICK zostawiamy w
    // akumulatorze i doślemy następnym flushem. Bez tego ogromny tick (biom 6
    // Fast Food + mikstura) odbijał się 400 i zatykał cały pipeline (patrz stała).
    const coinsDelta = coinsEarnedSinceLastFlush < MAX_COINS_PER_TICK
      ? coinsEarnedSinceLastFlush
      : MAX_COINS_PER_TICK;
    const destroyedDelta = destroyedSinceLastFlush;
    // Etap 5, 5.0.B: snap destroyedByType breakdown (do server gem roll).
    // Copy by spread bo reset akumulatora poniżej musi być atomowy z resetem
    // destroyedSinceLastFlush (oba liczone z tego samego eventu).
    const destroyedByType = { ...destroyedByTypeSinceLastFlush };
    if (coinsDelta === 0n && destroyedDelta === 0) return;
    // ODEJMUJEMY wysłaną porcję (nie zerujemy) — reszta monet ponad cap czeka na
    // kolejny flush. destroyed* idzie w całości z tym pierwszym tickiem.
    coinsEarnedSinceLastFlush -= coinsDelta;
    destroyedSinceLastFlush = 0;
    destroyedByTypeSinceLastFlush = {};
    tick = {
      tickId: makeTickId(),
      coinsDelta,
      destroyedDelta,
      destroyedByType,
      createdAt: Date.now(),
    };
    pendingTick = tick;
    // Została nadwyżka monet ponad cap → zaplanuj kolejny flush, by ją dosłać.
    if (coinsEarnedSinceLastFlush > 0n) markDirty();
  }

  try {
    const { currencies, eventCurrencies, stats, gemsRolled, save_rev } = await economyApi.worldTick(
      tick.tickId,
      tick.coinsDelta.toString(),
      tick.destroyedDelta,
      tick.destroyedByType,
    );
    pendingTick = null;
    // Faza 1a (dieta payloadu, DESIGN_PERFORMANCE_SCALING.md §A1): world/tick
    // zwraca tylko waluty + staty (NIE cały save). Patchujemy te pola do
    // lokalnego save'a — ekwipunek (pety/jajka) pozostaje nietknięty.
    // Faza 1b-1 drift-guard: `save_rev` — patch pomijany gdy <= appliedRev
    // (spóźniony/zduplikowany tick). `applied=false` → tick odrzucony.
    const applied = await saveManager.patchServerScalars({ currencies, stats, eventCurrencies }, save_rev);
    // HIGH 1 (Codex 1b-1): HUD (coins/gems/FX) aktualizujemy TYLKO gdy guard
    // przyjął tick. Odrzucony (stary) tick NIE może nadpisać HUD przez boczną
    // ścieżkę — nowszy stan już go ustawił; absolutne totale ze starej
    // odpowiedzi cofnęłyby waluty.
    if (applied) {
      // Reconciliation — `currencies.coins` to stan serwera PO doliczeniu ticka,
      // ale BEZ monet zebranych W TRAKCIE requestu (te są już w zresetowanym
      // `coinsEarnedSinceLastFlush`). Display = suma obu → brak flickera.
      const displayCoins = addCoins(currencies.coins, coinsEarnedSinceLastFlush);
      useGameStore.getState().setCoins(displayCoins);
      // Etap 5, 5.0.A — gems aktualizacja z server response po world tick.
      useGameStore.getState().setGems(currencies.gems);
      // EV-1b-2: portfel muszelek (server-authoritative) → plakietka HUD eventu.
      useGameStore.getState().setEventCurrencies(eventCurrencies);
      // Etap 5.0 polish (Codex gem-pickup LOW 1): FX 💎 odpalany z DOKŁADNEJ
      // kwoty wyrolowanej w TYM ticku (`gemsRolled` z response), NIE z delty
      // totalu — odporne na multi-tab / zewnętrzną synchronizację. Duplikat
      // (retry) zwraca 0, więc FX nie dubluje się przy resendzie.
      if (gemsRolled > 0) {
        eventBus.emit('gems:earned', { amount: gemsRolled });
      }
      eventBus.emit('coins:setExternal', { newAmount: displayCoins });
    } else {
      console.debug('[AutoSave] tick odrzucony przez drift-guard (stary save_rev) — pomijam sync HUD');
    }
    console.debug(
      `[AutoSave] world tick (${reason}) coins+=${tick.coinsDelta} destroyed+=${tick.destroyedDelta}`,
    );
  } catch (err) {
    // Codex MED 1: odróżnij błąd NIENAPRAWIALNY od PRZEJŚCIOWEGO. `400
    // validation_failed` = Zod odrzucił payload PRZED transakcją → serwer NA
    // PEWNO nie zaksięgował → resend tego samego payloadu nigdy nie przejdzie i
    // zatkałby cały pipeline (drift capa klient/serwer, destroyed>500, dowolny
    // błąd schematu). Przejściowy (5xx/timeout/sieć) → serwer mógł commitnąć,
    // więc resend TEN SAM tickId (dedup przez world_ticks) — bez zmian.
    const nonRetryable =
      err instanceof ApiError && err.status === 400 && err.code === 'validation_failed';
    if (nonRetryable && pendingTick) {
      // Bezpiecznie odzyskać monety do akumulatora (serwer ich nie dostał) —
      // zostaną ponownie pocięte ≤ cap przy następnym flushu. destroyed*
      // PORZUCAMY: to najpewniejszy sprawca 400 (przekroczony cap liczby), a ich
      // ponowne dodanie zapętliłoby ten sam błąd; tracimy max 1 tick gem-rolli
      // (rzadki edge, logowany). pendingTick czyścimy, by kolejny LEGALNY tick
      // ruszył zamiast wiecznie odbijać niepoprawny payload.
      console.error(
        `[AutoSave] world tick (${reason}) ODRZUCONY (400 validation_failed) — odzyskuję ` +
          `${pendingTick.coinsDelta} monet, porzucam destroyed=${pendingTick.destroyedDelta}, ` +
          `czyszczę pendingTick:`,
        err,
      );
      coinsEarnedSinceLastFlush += pendingTick.coinsDelta;
      pendingTick = null;
      markDirty();
      return;
    }
    console.warn(`[AutoSave] world tick (${reason}) failed — retry z tym samym tickId:`, err);
    // pendingTick ZOSTAJE — następny flush resend ten sam tickId. NIE
    // merge'ujemy delty do akumulatora (idempotency). markDirty planuje retry.
    markDirty();
  }
}

// ── Drain API dla granularnych akcji ekonomii (sesja 35B + 4.D.3) ───────────

/** Wypycha oczekujący world tick i czeka aż się zakończy (no-op gdy nic dirty). */
export function flushAutoSave(): Promise<void> {
  return flush('manual');
}

/** Pauzuje autosave — `doFlush` staje się no-opem (zachowuje `dirty` + akumulatory). */
export function pauseAutoSave(): void {
  paused = true;
}

/** Wznawia autosave. Gdy coś `dirty` uzbierało się w pauzie — planuje flush. */
export function resumeAutoSave(): void {
  paused = false;
  if (dirty) markDirty();
}

/**
 * Monety zebrane w świecie jeszcze NIE wysłane do `/world/tick` (sesja 4.D.3).
 * `economyActions.syncStoresFromSave` dodaje to do `save.coins` przy sync po
 * akcji ekonomii — bez tego inflight earnings zniknęłyby z HUD do next ticka.
 */
export function getPendingCoinsDelta(): bigint {
  return coinsEarnedSinceLastFlush;
}

/**
 * Odinstaluj bridge: clearuje timery, unsubskrybuje EventBus, resetuje state.
 * Idempotent.
 */
export function uninstallAutoSaveBridge(): void {
  if (!installed) return;
  if (debounceTimer != null) clearTimeout(debounceTimer);
  if (intervalTimer != null) clearInterval(intervalTimer);
  for (const off of unsubscribers) off();
  unsubscribers = [];
  debounceTimer = null;
  intervalTimer = null;
  destroyedSinceLastFlush = 0;
  destroyedByTypeSinceLastFlush = {};
  coinsEarnedSinceLastFlush = 0n;
  pendingTick = null;
  dirty = false;
  paused = false;
  flushLock = Promise.resolve();
  installed = false;
  console.info('[AutoSave] Bridge uninstalled');
}

export function installAutoSaveBridge(): void {
  if (installed) return;
  installed = true;

  // ── EventBus subskrypcje ──────────────────────────────────────────────────
  // `coins:earned` (delta realnego zarobku) — NIE `coins:changed` (total,
  // odpala się też przy `coins:setExternal` = sync po akcji ekonomii).
  unsubscribers.push(
    eventBus.on('coins:earned', (e) => {
      coinsEarnedSinceLastFlush += BigInt(e.amount);
      markDirty();
    }),
  );
  unsubscribers.push(
    eventBus.on('destructible:destroyed', (e) => {
      destroyedSinceLastFlush += 1;
      // Etap 5, 5.0.B: track per-type breakdown dla server gem roll.
      // Event ma `destructibleId` (np. 'dest-crystal') od dawna —
      // wcześniej AutoSave go ignorował, teraz akumuluje.
      destroyedByTypeSinceLastFlush[e.destructibleId] =
        (destroyedByTypeSinceLastFlush[e.destructibleId] ?? 0) + 1;
      markDirty();
    }),
  );

  // ── Interval safety net ──────────────────────────────────────────────────
  intervalTimer = setInterval(() => { void flush('interval'); }, INTERVAL_MS);

  // ── Visibility change — last chance flush gdy karta nieaktywna ────────────
  const onVisibility = (): void => {
    if (document.hidden) {
      void flush('visibility');
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
  unsubscribers.push(() => document.removeEventListener('visibilitychange', onVisibility));

  console.info('[AutoSave] Bridge installed — world tick: debounce=3s, interval=10s, visibility-flush');
}

// ── Vite HMR lifecycle ──────────────────────────────────────────────────────
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    uninstallAutoSaveBridge();
  });

  import.meta.hot.accept((newModule) => {
    newModule?.installAutoSaveBridge();
  });
}
